import React, { useContext, useEffect, useState } from 'react'
import { Content } from './style'
import LoadingComponent from '../../../components/LoadingComponent'
import { LeftSide, SectionName, SubHeaderContainer } from '../../../components/styles/subheader-style'
import { Outlet, useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import certificate from '../../../assets/certificates/SubHeader/certificate.svg'
import param from '../../../assets/certificates/SubHeader/param.svg'
import rnc from '../../../assets/home/receipt.svg'
import EditStructureModal from '../../../components/Modals/EditStructure'
import { ParamType } from './CreateParam'
import { Filter, Header } from '../../../components/SubHeaderFilter/types'
import { TypeMainOutlet } from '..'
import ApiService from '../../../services/ApiService'
import { PageHeader } from '../../../components/StandardPage'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import ModalContext from '../../../contexts/ModalContext'
import { OccurrenceContext } from '../../../contexts/OccurrenceContext'

export interface CertificateOutletType {
  setModal: React.Dispatch<React.SetStateAction<boolean>>
  param: ParamType
  setParam: React.Dispatch<React.SetStateAction<ParamType>>
  setClickedStructure: React.Dispatch<React.SetStateAction<number | null>>
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  appliedFilters: Filter[]
  search: string
  refresh: boolean
  setLoadingHeader: React.Dispatch<React.SetStateAction<boolean>>
}

const paramTableHeaders: Header[] = [
  { key: 'name', label: 'Nome do parâmetro', type: 'string' },
  { key: 'certificateType', label: 'Tipo de certificado', type: 'string' },
  { key: 'chemicalComposition', label: 'Composição química', type: 'string' },
  { key: 'mechanicalTests', label: 'Ensaios mecânicos', type: 'string' },
  { key: 'class', label: 'Condição', type: 'string' },
  { key: 'family', label: 'Família', type: 'string' },
  { key: 'astm', label: 'ASTM', type: 'string' },
  { key: 'uns', label: 'UNS', type: 'string' },
  { key: 'din', label: 'DIN', type: 'string' },
  { key: 'aisi', label: 'AISI', type: 'string' },
  { key: 'others', label: 'Outras', type: 'string' },
]

const tabs = [
  { path: '', image: certificate, name: 'Certificados' },
  { path: 'params', image: param, name: 'Parâmetros' },
  { path: 'rnc', image: rnc, name: 'RNC' },
]

const occurrenceHeaders: Header[] = [
  { key: 'id', label: 'ID', type: 'number' },
  { key: 'operationNumber', label: 'OP', type: 'string' },
  { key: 'companyName', label: 'Cliente', type: 'string' },
  { key: 'description', label: 'Descrição', type: 'string' },
  { key: 'area', label: 'Área', type: 'string' },
  { key: 'sector', label: 'Setor', type: 'string' },
  { key: 'stage', label: 'Etapa', type: 'string' },
  { key: 'quantity', label: 'Quantidade', type: 'number' },
  { key: 'cause', label: 'Causa', type: 'string' },
  { key: 'relevant', label: 'Pertinente', type: 'boolean' },
  { key: 'correction', label: 'Ação corretiva efetiva', type: 'string' },
  { key: 'providences', label: 'Responsável, descrição e prazo', type: 'string' },
  { key: 'allocation', label: 'Alocação', type: 'string' },
]

const emptyParam: ParamType = {
  structures: [],
  name: '',
  class: '',
  family: '',
  astm: '',
  uns: '',
  din: '',
  aisi: '',
  others: '',
}

const CertificatesContent: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [refresh, setRefresh] = useState<boolean>(false)
  const [loadingHeader, setLoadingHeader] = useState<boolean>(false)
  const { search } = useOutletContext<TypeMainOutlet>()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fromParam = searchParams.get('from')
  const attachmentIdParam = searchParams.get('attachmentId')

  const handleBack = () => {
    if (fromParam === 'attachments' && attachmentIdParam) {
      navigate(`/panel/attachments?attachmentId=${attachmentIdParam}`)
      return
    }
    if (fromParam === 'attachments') {
      navigate('/panel/attachments')
      return
    }
    navigate('/panel/certificates')
  }
  const { setModalSection, setModalActive } = useContext(ModalContext)
  const { setOccurrence } = useContext(OccurrenceContext)
  const [paramState, setParamState] = useState<ParamType>(emptyParam)
  const [modal, setModal] = useState<boolean>(false)
  const [clickedStructure, setClickedStructure] = useState<number | null>(null)
  const [dynamicCertificateHeaders, setDynamicCertificateHeaders] = useState<Header[]>([
    { key: 'param', label: 'Parâmetro', type: 'string' },
    { key: 'supplier', label: 'Fornecedor', type: 'string' },
    { key: 'customer', label: 'Cliente', type: 'string' },
    { key: 'heatNumber', label: 'Nº da corrida', type: 'string' },
    { key: 'codeOpString', label: 'Ordem de produção', type: 'string' },
    { key: 'purchaseOrderNumber', label: 'Nº Pedido de compra do cliente', type: 'string' },
    { key: 'standard', label: 'Norma', type: 'string' },
    { key: 'invoiceNumber', label: 'Nota fiscal', type: 'string' },
    { key: 'startingMaterial', label: 'Material de partida', type: 'string' },
    { key: 'description', label: 'Descrição', type: 'string' },
    { key: 'heatTreatment', label: 'Tratamento térmico', type: 'string' },
    { key: 'factory', label: 'Usina de fabricação', type: 'string' },
    { key: 'observations', label: 'Observações', type: 'string' },
    { key: 'mechanicalTests', label: 'Ensaios mecânicos', type: 'string' },
    { key: 'chemicalComposition', label: 'Composição química', type: 'string' },
    { key: 'type', label: 'Tipo do certificado', type: 'string', selectOptions: [] },
    { key: 'date', label: 'Data de criação', type: 'date' },
  ])

  const tabIndex = location.pathname.includes('/panel/certificates/rnc')
    ? 2
    : location.pathname.includes('/panel/certificates/params')
      ? 1
      : 0
  const isCreateRoute = location.pathname.includes('create') && !location.pathname.includes('edit')
  const isEditRoute = location.pathname.includes('edit')

  const certificateFiltersState = usePersistedFilters('certificates')
  const paramFiltersState = usePersistedFilters('certificate_params')
  const occurrenceFiltersState = usePersistedFilters('occurrences')
  const activeFiltersState = tabIndex === 2 ? occurrenceFiltersState : tabIndex === 1 ? paramFiltersState : certificateFiltersState

  useEffect(() => {
    if (tabIndex === 0) {
      ApiService.certificates.getTypes()
        .then((response: any) => {
          if (response.data && Array.isArray(response.data)) {
            const options: { value: string; label: string }[] = []
            response.data.forEach((item: any) => {
              if (item.name === 'Origem / Qualidade' || item.id === 1) {
                options.push({ value: 'Origem', label: 'Origem' })
                options.push({ value: 'Qualidade', label: 'Qualidade' })
              } else {
                options.push({ value: item.name, label: item.name })
              }
            })
            setDynamicCertificateHeaders(prev =>
              prev.map(h => h.key === 'type' ? { ...h, selectOptions: options } : h)
            )
          }
        })
        .catch((error: any) => {
          console.error(error)
        })
    }
  }, [tabIndex])

  useEffect(() => {
    setParamState(emptyParam)
  }, [tabIndex])

  const handleNewOccurrence = () => {
    setOccurrence(null)
    setModalSection('createEditOccurrenceModal')
    setModalActive(true)
  }

  const outletContext: CertificateOutletType = {
    setModal,
    param: paramState,
    setParam: setParamState,
    setClickedStructure,
    setLoading,
    appliedFilters: activeFiltersState.appliedFilters,
    search,
    refresh,
    setLoadingHeader,
  }

  return (
    <>
      {isCreateRoute ? (
        <SubHeaderContainer>
          <LeftSide>
            <SectionName>{tabIndex === 1 ? 'Parâmetro da norma' : 'Novo certificado'}</SectionName>
          </LeftSide>
        </SubHeaderContainer>
      ) : (
        <PageHeader
          title={isEditRoute ? (tabIndex === 1 ? 'Parâmetro da norma' : 'Editar certificado') : 'Qualidade'}
          tableName={tabIndex === 2 ? 'Occurrence' : tabIndex === 1 ? 'CertificateParam' : 'Certificate'}
          moduleName={tabIndex === 2 ? 'Ocorrências' : tabIndex === 1 ? 'Parâmetros da Norma' : 'Certificados'}
          tabs={tabs.map((tab) => ({ key: tab.path, label: tab.name, icon: tab.image }))}
          activeTab={tabs[tabIndex].path}
          onTabChange={(key) => {
            if (isEditRoute || key !== tabs[tabIndex].path) navigate(`/panel/certificates/${key}`)
          }}
          leftExtra={
            isEditRoute ? (
              <button
                type="button"
                onClick={handleBack}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#ffffff',
                  color: '#475569',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  marginLeft: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
              >
                <ArrowLeft size={14} />
                Voltar
              </button>
            ) : undefined
          }
          onReload={() => setRefresh((p) => !p)}
          isLoading={loadingHeader}
          filterHeaders={tabIndex === 2 ? occurrenceHeaders : tabIndex === 1 ? paramTableHeaders : dynamicCertificateHeaders}
          onApplyFilter={activeFiltersState.handleApplyFilter}
          appliedFilters={activeFiltersState.appliedFilters}
          onRemoveFilter={activeFiltersState.handleRemoveFilter}
          onClearAllFilters={activeFiltersState.handleClearAll}
          createButton={{
            label: tabIndex === 0 ? 'Criar certificado' : tabIndex === 1 ? 'Criar parâmetro' : 'Nova Ocorrência',
            onClick: () => {
              if (tabIndex === 0) navigate('/panel/certificates/create')
              else if (tabIndex === 1) navigate('/panel/certificates/params/create')
              else handleNewOccurrence()
            },
          }}
        />
      )}
      {loading === true && <LoadingComponent />}
      <Content>
        <Outlet context={outletContext} />
      </Content>
      {modal &&
      <EditStructureModal
        onClose={() => setModal(false)}
        param={paramState}
        setParam={setParamState}
        clickedStructure={clickedStructure || 0}
      />}
    </>
  )
}
export default CertificatesContent
